import { useMutationSessionPackSubscriptionPendingStartDate } from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { ModalContext, useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { DateTime } from "luxon";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../..";
import CustomTooltip from "../../CustomTooltip";
import { PlaySquareIcon } from "../../icons/PlaySquareIcon";
import { Power01Icon } from "../../icons/Power01Icon";
import { CreditPackItemType } from "../CreditPackItem";
import { CreditPackMenuPopover } from "./CreditPackMenuPopover";
import { EditExpiryDateModal } from "./EditExpiryDateModal";

type CreditPackActionsProps = {
  activeTabId: string;
  hideActions: boolean;
  id: number;
  userMemberId: string;
  creditPack: CreditPackItemType;
};

export const CreditPackActions = ({
  activeTabId,
  hideActions,
  id,
  userMemberId,
  creditPack,
}: CreditPackActionsProps) => {
  const { api } = useGymflowModels();
  const {
    club: { timezone },
  } = useClubSettings();
  const { t } = useTranslation();
  const { setModal, hide: hideModal } = useContext(ModalContext);

  const { mutateAsync: mutateAsyncSessionPackSubscriptionPendingStartDate } =
    useMutationSessionPackSubscriptionPendingStartDate({ api });

  const handleUpdatePendingStartDate = useCallback(async () => {
    const startDate = DateTime.now().setZone(timezone).toISODate() ?? "";
    const body = {
      subscriptionId: id,
      userMemberId: userMemberId,
      startDate,
    };
    await mutateAsyncSessionPackSubscriptionPendingStartDate(body);
  }, [
    id,
    mutateAsyncSessionPackSubscriptionPendingStartDate,
    timezone,
    userMemberId,
  ]);

  if (activeTabId === "CURRENT") {
    return (
      <>
        {creditPack.status === "PENDING" &&
        !creditPack.partOfMembership &&
        !hideActions ? (
          <CustomTooltip
            message={t("components.creditPackItem.startImmediately")}
            hideIfEmpty
            sideOffset={12}
          >
            <Button
              className="size-9 p-0"
              onClick={handleUpdatePendingStartDate}
            >
              <PlaySquareIcon className="pointer-events-auto size-5 text-gray-950" />
            </Button>
          </CustomTooltip>
        ) : null}
        <CreditPackMenuPopover
          creditPack={creditPack}
          hideActions={hideActions}
          id={id}
          userMemberId={userMemberId}
        />
      </>
    );
  }

  return (
    <Button
      className={cn("p-0 w-9 h-9", { hidden: hideActions })}
      hidden={creditPack.partOfMembership}
      onClick={() => {
        if (hideActions) return;
        setModal(
          <EditExpiryDateModal
            hide={() => hideModal()}
            isActivate
            subscriptionId={id}
            userMemberId={userMemberId}
          />,
        );
      }}
    >
      <Power01Icon
        className="size-5"
        pathClassName="stroke-gray-950 dark:stroke-gray-25"
      />
    </Button>
  );
};
